import img from "../assets/images/shutterstock_699442570-min.jpg";
import { useState } from "react";
import constants from "../constants/constants";
import SEO, { pageSEO } from "../components/seo.component";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../functions/animations";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChartLine, faFileLines, faHouse } from "@fortawesome/free-solid-svg-icons";

export default function Reports() {
  const [popup, setPopup] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    clientType: "",
    interests: [],
  });

  const seoConfig = pageSEO.reports();

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCheckboxChange = (e) => {
    const { value, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      interests: checked
        ? [...prev.interests, value]
        : prev.interests.filter((item) => item !== value),
    }));
  };

  async function handleSubmit(event) {
    event.preventDefault();

    const submissionData = {
      access_key: constants.EMAIL_ACCESS_KEY,
      subject: "New Lead Interested in Real Estate Reports",
      Name: formData.name,
      "Email Address": formData.email,
      "Client Type": formData.clientType,
      "Interested in": formData.interests,
    };

    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(submissionData),
      });

      const data = await res.json();

      setPopup(
        data?.success
          ? {
            subject: "Thanks for your message!",
            message: "We will get back to you as soon as possible.",
          }
          : {
            subject: "Something went wrong",
            message: "Please try again later.",
          }
      );
    } catch {
      setPopup({
        subject: "Something went wrong",
        message: "Please try again later.",
      });
    }
  }

  return (
    <>
      <SEO {...seoConfig} />

      <section className="relative overflow-hidden bg-cottage-secondary pt-40 pb-28">
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="relative mx-auto max-w-6xl px-6 text-center"
        >
          <motion.p
            variants={fadeUp}
            className="uppercase tracking-[0.5em] font-semibold text-cottage-accent"
          >
            Exclusive Resources
          </motion.p>

          <motion.h1
            variants={fadeUp}
            className="mt-6 font-serif text-5xl leading-none text-cottage-primary lg:text-7xl"
          >
            Real Estate
            <br />
            Reports
          </motion.h1>

          <motion.p
            variants={fadeUp}
            className="mx-auto mt-8 max-w-3xl text-lg leading-8 text-cottage-muted"
          >
            Receive valuable market updates, buying insights, and selling
            strategies designed to help you make confident real estate
            decisions.
          </motion.p>
        </motion.div>
      </section>

      <section className="mx-auto max-w-[1400px] px-6 py-24">
        <div className="grid gap-12 lg:grid-cols-[480px_1fr]">

          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="hidden lg:block"
          >
            <div className="sticky top-32">
              <div className="overflow-hidden rounded-lg shadow-xl">
                <img
                  src={img}
                  alt="Real Estate Reports"
                  className="h-[600px] w-full object-cover transition duration-700 hover:scale-105"
                />
              </div>
            </div>
          </motion.div>

          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <div className="rounded-lg border border-cottage-border bg-white p-8 md:p-12">

              <h2 className="font-serif text-4xl text-cottage-primary">
                Request Your Reports
              </h2>

              <p className="mt-4 leading-7 text-cottage-muted">
                Tell us what information you are looking for and we will send
                personalized resources directly to your inbox.
              </p>

              <form
                onSubmit={handleSubmit}
                className="mt-10 space-y-6"
              >
                <div className="grid gap-6 md:grid-cols-2">
                  <input
                    required
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Full Name"
                    className="w-full rounded-2xl border border-cottage-border bg-cottage-secondary/30 px-5 py-4 outline-none focus:border-cottage-accent focus:bg-white"
                  />

                  <input
                    required
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Email Address"
                    className="w-full rounded-2xl border border-cottage-border bg-cottage-secondary/30 px-5 py-4 outline-none focus:border-cottage-accent focus:bg-white"
                  />

                </div>


                <select
                  required
                  name="clientType"
                  value={formData.clientType}
                  onChange={handleInputChange}
                  className="w-full rounded-2xl border border-cottage-border bg-cottage-secondary/30 px-5 py-4 outline-none focus:border-cottage-accent"
                >
                  <option value="">
                    I am a
                  </option>
                  <option>
                    Buyer
                  </option>
                  <option>
                    Seller
                  </option>
                  <option>
                    Renter
                  </option>
                  <option>
                    Other
                  </option>
                </select>


                <div>

                  <p className="mb-4 text-sm uppercase tracking-widest text-cottage-muted">
                    Interested In
                  </p>

                  <div className="grid gap-4 md:grid-cols-2">

                    {[
                      "Buying guides",
                      "Selling guides",
                      "Choosing realtor",
                      "Renovations",
                    ].map((item) => (

                      <label
                        key={item}
                        className={`cursor-pointer rounded-2xl border p-5 transition ${formData.interests.includes(item)
                          ? "border-cottage-accent bg-cottage-secondary"
                          : "border-cottage-border hover:border-cottage-accent"
                          }`}
                      >

                        <div className="flex items-center gap-3">

                          <input
                            type="checkbox"
                            value={item}
                            checked={formData.interests.includes(item)}
                            onChange={handleCheckboxChange}
                            className="h-5 w-5 accent-cottage-primary"
                          />

                          <span className="text-sm text-cottage-primary">
                            {item}
                          </span>

                        </div>

                      </label>

                    ))}

                  </div>

                </div>


                <button className="btn-cottage">
                  Request Reports
                </button>

              </form>

            </div>

          </motion.div>

        </div>
      </section>


      {popup && (
        <>
          <div className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm" />

          <div className="fixed left-1/2 top-1/2 z-50 w-[90%] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-3xl bg-white p-10 text-center shadow-2xl">

            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-cottage-secondary text-2xl text-cottage-primary">
              ✓
            </div>

            <h3 className="mt-6 font-heading text-3xl text-cottage-primary">
              {popup.subject}
            </h3>

            <p className="mt-4 leading-7 text-cottage-muted">
              {popup.message}
            </p>

            <button
              onClick={() => setPopup(false)}
              className="btn-cottage mt-8"
            >
              Close
            </button>

          </div>
        </>
      )}

    </>
  );
}